import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { LegacyShimsModule } from 'apps/account/src/account-shared/legacy-shims/legacy-shims.module';
import { FtModalComponent } from '../../../common/components/modal/ft-modal.component';
import { FtModalChromeComponent } from '../../../common/components/modal/ft-modal-chrome.component';
import { ShareType } from '../../../../account-v2/src/app/components/broadcast/shareType';
import { StoryDescriptor } from '../../../../account-v2/src/app/components/broadcast/storyDescriptor';
import { BroadcastFlowBase, BroadcastFlowStep } from './broadcast-flow-base';
import { FtBroadcastChooseGoalComponent } from './ft-broadcast-choose-goal.component';
import { FtBroadcastChooseNetworkComponent } from './ft-broadcast-choose-network.component';
import { FtBroadcastShareImageReadyComponent } from './ft-broadcast-share-image-ready.component';
import { FtStoryImageSetupComponent } from './ft-story-image-setup.component';

/**
 * Broadcast-from-story-modal flow host — loads the story and contests, then walks
 * the goal → image-setup → channel → share steps as per-step *ngIf views.
 * Standalone — mounted via createComponent.
 */
@Component({
    selector: 'ft-broadcast-story-modal-ng',
    imports: [
        TranslateModule,
        LegacyShimsModule,
        FtModalComponent,
        FtModalChromeComponent,
        FtBroadcastChooseGoalComponent,
        FtBroadcastChooseNetworkComponent,
        FtBroadcastShareImageReadyComponent,
        FtStoryImageSetupComponent,
    ],
    encapsulation: ViewEncapsulation.None,
    templateUrl: './ft-broadcast-story-modal.component.html',
})
export class FtBroadcastStoryModalComponent extends BroadcastFlowBase implements OnInit, OnDestroy {
    ngOnInit(): void {
        // The builder's step list (ChooseGoal starts active).
        this.steps = [
            new BroadcastFlowStep('ChooseGoal', true),
            new BroadcastFlowStep('StoryShareImageSetup'),
            new BroadcastFlowStep('PhotocontestInvitationShareImageSetup'),
            new BroadcastFlowStep('ChooseNetwork'),
            new BroadcastFlowStep('CreateImageProgress'),
            new BroadcastFlowStep('ShareImageReady'),
        ];

        // ngStrap put these on <body> while the modal was shown
        // (`.modal-open { overflow: hidden }` — scroll-lock parity).
        document.body.classList.add('modal-open', 'modal-with-am-fade');

        // Verbatim BroadcastStoryFromStoryModalFlowController ctor body.
        this.property = this.sessionService.getProperty(this.propertyUuid());
        // WS2 Step 5 (ng-redux ui.context teardown): singleProperty always === SpacesService.getPropertyUuid();
        // organization never existed in the context reducer (always undefined). Byte-identical "/undefined/<uuid>".
        this.fullContext = `/undefined/${this.spacesService.getPropertyUuid()}`;

        Promise.all([
            this.photoContest.details(this.propertyUuid(), this.storyUuid()),
            this.layersApi.photocontest({ propertyId: this.propertyUuid() }),
        ]).then((datas: any[]) => {
            this.zone.run(() => {
                const story = datas[0],
                    photoContestLayer = datas[1];
                this.storyDescriptor = new StoryDescriptor(this.parse, story);
                this.storySharedTo = story.socialNetworksSharing
                    .filter((n: any) => this.parse.intOrDefault(n.totalShareTimes) > 0)
                    .map((n: any) => this.parse.intOrDefault(n.socialNetworkType));
                this.contests = this.parse.arrayOrDefault(photoContestLayer.contests);
                this.isQuoteOnly = !story.guest.poststay.photo;

                this.imageSetupOptions = this.createImageSetupOptionsFromStory(story);

                const hasActiveContests =
                    !!this.contests &&
                    !!this.contests.find(
                        (c: any) => this.parse.bool(c.isActive) && !this.parse.bool(c.isClosed) && this.daysUntilDate(c.endDate) >= 1,
                    );
                if (!hasActiveContests && !this.isQuoteOnly) {
                    this.startLeadShare();
                }

                Promise.resolve(
                    !!story.guest.poststay.photo
                        ? this.photoContest.load({ contestId: story.guest.poststay.photo.photoContest.uuid, propertyUuid: this.propertyUuid() })
                        : null,
                ).then((contest: any) => {
                    this.zone.run(() => {
                        this.contest = contest;
                        this.story = story;
                        this.isInitialized = true;
                    });
                });
            });
        });
    }

    ngOnDestroy(): void {
        document.body.classList.remove('modal-open', 'modal-with-am-fade');
    }

    protected chooseDifferentGoal(): void {
        this.moveTo(BroadcastFlowBase.STEP_NAME_CHOOSE_GOAL);
    }

    protected startLeadShare(): void {
        this.setShareType(ShareType.Story);
        this.moveTo(BroadcastFlowBase.STEP_NAME_STORY_IMAGE_SETUP);
    }

    protected startStorytellersShare(contest: any): void {
        this.inviteToContest = contest;
        this.setShareType(ShareType.PhotoContestInvitation);
        this.moveTo(BroadcastFlowBase.STEP_NAME_PHOTOCONTEST_INVITATION_IMAGE_SETUP);
    }

    protected startWebsiteInviteShare(): void {
        this.setShareType(ShareType.Website);
        this.moveTo(BroadcastFlowBase.STEP_NAME_STORY_IMAGE_SETUP);
    }

    protected returnToImageSetup(): void {
        this.moveTo(
            this.shareType == ShareType.Story || this.shareType == ShareType.Website
                ? BroadcastFlowBase.STEP_NAME_STORY_IMAGE_SETUP
                : BroadcastFlowBase.STEP_NAME_PHOTOCONTEST_INVITATION_IMAGE_SETUP,
        );
    }

    protected finishImageSetup(): void {
        this.moveTo(BroadcastFlowBase.STEP_NAME_CHOOSE_NETWORK);
    }
}
